import React, { useState, useContext } from "react";
import { Form, Select, Divider, Card, Spin, Skeleton, Col, Row, Switch, TimePicker } from "antd";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import styled from "styled-components";
import dayjs from "dayjs";

import { required } from "../../../../lib/utils/formValidation";
import transcodingInputURLDefaultValues from "../../../../lib/utils/defaultValues/transcodingInputURLDefaultValues";
import { MAIN_CONNECTION_TYPE } from "../../../../lib/utils/types/inputURLTypes";
import { CAPABILITIES_TYPE } from "../../../../lib/utils/types/capabilitiesTypes";

import { selectors as NODE_DEVICES_SELECTORS } from "../../../../ducks/nodeDevices";

import useNodeChannelConfig from "../../../../hooks/useNodeChannelConfig";

import {
  INPUT_DECKLINK_TYPE,
  INPUT_URL_TYPE,
  INPUT_WEBCAM_TYPE,
  SELECT_DEFAULT_AUTO,
  INPUT_TYPES,
  NODE_DEVICES_TYPE,
  INPUT_HLS_SELECTOR,
} from "../../../../lib/utils/constants";
import InputTranscodingStreamURLForm from "./InputTranscodingStreamURLForm";
import UpdateNodeDevices from "../../../UpdateNodeDevices";

import InputSDI from "../InputsStream/InputSDI";
import InputWebcam from "../InputsStream/InputWebcam";
import InputRTMP from "../InputsStream/InputRTMP";
import InputRTP from "../InputsStream/InputRTP";
import InputNDI from "../InputsStream/InputNDI";
import InputHLS from "../InputsStream/InputHLS";
import ForceRestartHourLabel from "../../../ForceRestartHourLabel";

import { selectors as NODE_SELECTORS } from "../../../../ducks/node";
import { FormV5Context } from "../../NodeChannelFormV5";
import InputNumberField from "../../../Fields/InputNumberField";

import globalTranslations from "../../../../lib/translations";

const { Item } = Form;
const { Option } = Select;

const translations = defineMessages({
  bufferFrames: {
    id: "InputTranscodingStreamURLForm.bufferFrames",
    defaultMessage: "Buffer frames",
  },
  connectTimeoutMs: {
    id: "InputTranscodingStreamURLForm.connectTimeoutMs",
    defaultMessage: "Connect timeout [ms]",
  },
  getFrameTimeoutMs: {
    id: "InputTranscodingStreamURLForm.getFrameTimeoutMs",
    defaultMessage: "Frame timeout [ms]",
  },
  reinitDelayMs: {
    id: "InputTranscodingStreamURLForm.reinitDelayMs",
    defaultMessage: "Reinitialization delay [ms]",
  },
  maxInterstreamDistanceMs: {
    id: "InputTranscodingStreamURLForm.maxInterstreamDistanceMs",
    defaultMessage: "Max interstream distance [ms]",
  },
  forceRestartHour: {
    id: "InputTranscodingStreamURLForm.forceRestartHour",
    defaultMessage: "Force Restart Hour",
  },
  selectHour: {
    id: "InputTranscodingStreamURLForm.selectHour",
    defaultMessage: "Select hour",
  },
});

function InputTranscodingSectionContainer({
  capabilities,
  decklinkInputs,
  disabled,
  initialValue,
  nodeDevicesLoaded,
  setSelectedInputType,
  sharedChannelList,
  stundAddress,
  webcamAudioInputs,
  webcamVideoInputs,
  prefix,
}) {
  const form = useContext(FormV5Context);
  const { getFieldValue, setFieldsValue, validateFields, setFieldValue } = form;
  const isSwitchForceRestart = Form.useWatch([...prefix, "forceRestart"], form) || false;

  const [inputType, setInputType] = useState(getFieldValue([...prefix, "mainInput", "type"]));
  const { isEnginePlayout } = useNodeChannelConfig();
  const { formatMessage } = useIntl();

  const handleChangeInputType = (value) => {
    setSelectedInputType(value);
    const formData = getFieldValue();
    delete formData.mainInput;

    if (value === INPUT_DECKLINK_TYPE.value) {
      setFieldsValue({
        mainInput: {
          type: value,
          format: SELECT_DEFAULT_AUTO.value,
          audioChannels: 2,
          audioSource: SELECT_DEFAULT_AUTO.value,
          videoSource: SELECT_DEFAULT_AUTO.value,
        },
      });
    }

    if (value === INPUT_URL_TYPE.value) {
      const initialInputValues = transcodingInputURLDefaultValues({
        input: initialValue,
      });
      setFieldsValue({ mainInput: initialInputValues });
    }

    if (value === INPUT_WEBCAM_TYPE.value) {
      setFieldsValue({ mainInput: { type: value } });
    }

    setFieldsValue({ mainInput: { type: value } });
    setInputType(value);
  };

  const isInputURLTypeSelected = inputType === INPUT_URL_TYPE.value;
  const isInputSDITypeSelected = inputType === INPUT_DECKLINK_TYPE.value;
  const isInputWEBCAMTypeSelected = inputType === INPUT_WEBCAM_TYPE.value;
  const isInputRTMPTypeSelected = inputType === INPUT_TYPES.inputRtmp.value;
  const isInputRTPTypeSelected = inputType === INPUT_TYPES.inputMpegtsOverRtp.value;
  const isInputNDITypeSelected = inputType === INPUT_TYPES.inputNdi.value;
  const isInputHLSTypeSelected = inputType === INPUT_HLS_SELECTOR.value;

  const isDecklinkInputs = decklinkInputs.length > 0;
  const isWebcamAudioInputs = webcamAudioInputs.length > 0;
  const isWebcamVideoInputs = webcamVideoInputs.length > 0;

  const INPUT_TYPES_ARRAY = Object.values(INPUT_TYPES);
  const INPUT_STREAM_DEVICE_TYPE = [...INPUT_TYPES_ARRAY, INPUT_HLS_SELECTOR];

  const checkIfOptionDisabled = (optionValue) => {
    if (capabilities?.inputTypes && !capabilities?.inputTypes.includes(optionValue)) {
      return true;
    }
    if (optionValue === INPUT_DECKLINK_TYPE.value && !isDecklinkInputs) {
      return true;
    }

    if (optionValue === INPUT_WEBCAM_TYPE.value && (!isWebcamAudioInputs || !isWebcamVideoInputs)) {
      return true;
    }

    return false;
  };

  const handleSwitchForceRestart = (value) => {
    const formFieldValues = getFieldValue(prefix);
    if (!value) {
      delete formFieldValues.forceRestartHour;
      setFieldValue(prefix, { ...formFieldValues, forceRestart: value });
    }
  };

  const handleForceRestartHourChange = (_time, timeString) => {
    setFieldValue([...prefix, "forceRestartHour"], timeString);
  };

  return (
    <Card extra={<UpdateNodeDevices />}>
      {isEnginePlayout && (
        <Row>
          <Col span={8}>
            <InputNumberField
              name={[...prefix, "bufferFrames"]}
              disabled={disabled}
              label={formatMessage(translations.bufferFrames)}
              rules={[required]}
            />
          </Col>
          <Col span={8}>
            <InputNumberField
              name={[...prefix, "connectTimeoutMs"]}
              disabled={disabled}
              label={formatMessage(translations.connectTimeoutMs)}
              rules={[required]}
            />
          </Col>
          <Col span={8}>
            <InputNumberField
              name={[...prefix, "getFrameTimeoutMs"]}
              disabled={disabled}
              label={formatMessage(translations.getFrameTimeoutMs)}
              rules={[required]}
            />
          </Col>
          <Col span={8}>
            <InputNumberField
              name={[...prefix, "reinitDelayMs"]}
              disabled={disabled}
              label={formatMessage(translations.reinitDelayMs)}
              rules={[required]}
            />
          </Col>
          <Col span={8}>
            <InputNumberField
              name={[...prefix, "maxInterstreamDistanceMs"]}
              disabled={disabled}
              label={formatMessage(translations.maxInterstreamDistanceMs)}
              rules={[required]}
            />
          </Col>

          <Col span={24}>
            <Row>
              <Col span={8}>
                <Item
                  name={[...prefix, "forceRestart"]}
                  valuePropName="checked"
                  label={<FormattedMessage id="GeneralVideoCodec.forceRestart" defaultMessage="Force Restart" />}
                  rules={[required]}
                >
                  <Switch
                    disabled={disabled}
                    onChange={handleSwitchForceRestart}
                    checkedChildren={formatMessage(globalTranslations.on)}
                    unCheckedChildren={formatMessage(globalTranslations.off)}
                  />
                </Item>
              </Col>
              {isSwitchForceRestart && (
                <Col span={8}>
                  <Item
                    name={[...prefix, "forceRestartHour"]}
                    label={<ForceRestartHourLabel />}
                    rules={[required]}
                    getValueProps={(value) => ({
                      value: value ? dayjs(value, "HH:mm") : null,
                    })}
                    normalize={(value) => value && dayjs(value).format("HH:mm")}
                  >
                    <TimePicker
                      format="HH:mm"
                      needConfirm={false}
                      showNow={false}
                      disabled={disabled}
                      placeholder={formatMessage(translations.selectHour)}
                      onChange={handleForceRestartHourChange}
                    />
                  </Item>
                </Col>
              )}
            </Row>
          </Col>
        </Row>
      )}
      <Item
        name={[...prefix, "mainInput", "type"]}
        label={<FormattedMessage id="InputTranscodingSectionContainer.type" defaultMessage="Type" />}
        rules={[required]}
      >
        <Select disabled={disabled} onChange={handleChangeInputType}>
          {INPUT_STREAM_DEVICE_TYPE.map((option) => (
            <Option key={option.value} value={option.value} disabled={checkIfOptionDisabled(option.value)}>
              {option.label}
            </Option>
          ))}
        </Select>
      </Item>
      {nodeDevicesLoaded && (
        <>
          <StyledSpin
            tip={
              <FormattedMessage
                id="InputTranscodingSectionContainer.loadingNodeDevices"
                defaultMessage="Loading node devices list..."
              />
            }
          >
            <Skeleton active />
          </StyledSpin>
          <Divider />
        </>
      )}
      {isInputURLTypeSelected && (
        <InputTranscodingStreamURLForm
          channelList={sharedChannelList}
          disabled={disabled}
          initialValue={initialValue}
          sharedChannelList={sharedChannelList}
          stundAddress={stundAddress}
          prefix={prefix}
        />
      )}
      {isInputRTMPTypeSelected && <InputRTMP prefix={[...prefix, "mainInput"]} disabled={disabled} />}
      {isInputRTPTypeSelected && <InputRTP disabled={disabled} prefix={[...prefix, "mainInput"]} />}
      {isInputNDITypeSelected && (
        <InputNDI
          disabled={disabled}
          initialValue={{ streamSource: initialValue?.streamSource, streamAddr: initialValue?.streamAddr }}
          prefix={[...prefix, "mainInput"]}
        />
      )}
      {isInputWEBCAMTypeSelected && (
        <InputWebcam
          audioInputs={webcamAudioInputs}
          disabled={disabled}
          getFieldValue={getFieldValue}
          setFieldsValue={setFieldsValue}
          videoInputs={webcamVideoInputs}
          validateFields={validateFields}
          prefix={[...prefix, "mainInput"]}
        />
      )}
      {isInputSDITypeSelected && (
        <InputSDI
          getFieldValue={getFieldValue}
          inputs={decklinkInputs}
          disabled={disabled}
          type={NODE_DEVICES_TYPE.decklinkInputs}
          setFieldsValue={setFieldsValue}
          prefix={[...prefix, "mainInput"]}
        />
      )}
      {isInputHLSTypeSelected && <InputHLS prefix={[...prefix, "mainInput"]} disabled={disabled} />}
    </Card>
  );
}

const StyledSpin = styled(Spin)`
  width: 100%;
`;

InputTranscodingSectionContainer.propTypes = {
  capabilities: PropTypes.shape(CAPABILITIES_TYPE),
  disabled: PropTypes.bool,
  decklinkInputs: PropTypes.arrayOf(
    PropTypes.shape({
      desc: PropTypes.string,
      device: PropTypes.string,
    })
  ).isRequired,
  initialValue: PropTypes.shape({
    type: PropTypes.string.isRequired,
    switchInputBackup: PropTypes.bool,
    ...MAIN_CONNECTION_TYPE,
  }),
  stundAddress: PropTypes.string,
  sharedChannelList: PropTypes.array,
  webcamAudioInputs: PropTypes.arrayOf(
    PropTypes.shape({
      device: PropTypes.string,
    })
  ).isRequired,
  webcamVideoInputs: PropTypes.arrayOf(
    PropTypes.shape({
      device: PropTypes.string,
    })
  ).isRequired,
  nodeDevicesLoaded: PropTypes.bool,
  setSelectedInputType: PropTypes.func.isRequired,
  prefix: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
};

InputTranscodingSectionContainer.defaultProps = {
  capabilities: null,
  disabled: null,
  initialValue: null,
  nodeDevicesLoaded: null,
  sharedChannelList: null,
  stundAddress: null,
  prefix: null,
};

const mapStateToProps = (state) => ({
  capabilities: NODE_SELECTORS.getNodeCapabilities(state),
  decklinkInputs: NODE_DEVICES_SELECTORS.getDecklinkInputs(state),
  decklinkOutputs: NODE_DEVICES_SELECTORS.getDecklinkOutputs(state),
  webcamAudioInputs: NODE_DEVICES_SELECTORS.getWebcamAudioInputs(state),
  webcamVideoInputs: NODE_DEVICES_SELECTORS.getWebcamVideoInputs(state),
  nodeDevicesLoaded: NODE_DEVICES_SELECTORS.getLoadingDevices(state),
});

export default connect(mapStateToProps, null)(InputTranscodingSectionContainer);
